import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Box,
  TextField,
  Avatar,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useAuth } from '../context/AuthContext';
import userService from '../services/userService';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || ''
      });
      if (user.profilePhoto) {
        setProfilePhotoPreview(user.profilePhoto);
      }
    }
  }, [user]);

  const handleChange = (e) => {
    setProfile({
      ...profile,
      [e.target.name]: e.target.value
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Veuillez sélectionner une image valide' });
        return;
      }
      
      // Vérifier la taille (max 50MB - sera compressé en WebP)
      if (file.size > 50 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'L\'image ne doit pas dépasser 50MB' });
        return;
      }

      setProfilePhoto(file);
      
      // Créer une prévisualisation
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const updatedUser = await userService.updateProfile(profile, profilePhoto);
      
      // Mettre à jour le contexte utilisateur
      const updatedUserData = {
        ...user,
        name: updatedUser.name,
        email: updatedUser.email,
        phone: updatedUser.phone,
        profilePhoto: updatedUser.profilePhoto,
        signupMethod: updatedUser.signupMethod,
        isEmailVerified: updatedUser.isEmailVerified
      };
      updateUser(updatedUserData);
      
      // Mettre à jour l'état local
      setProfilePhoto(null);
      if (updatedUser.profilePhoto) {
        setProfilePhotoPreview(updatedUser.profilePhoto);
      }
      
      // Vérifier si un email de vérification a été envoyé
      if (user?.signupMethod === 'phone' && profile.email && profile.email !== user?.email) {
        setMessage({ 
          type: 'success', 
          text: 'Profil mis à jour avec succès. Un email de vérification a été envoyé à votre nouvelle adresse email.' 
        });
      } else {
        setMessage({ type: 'success', text: 'Profil mis à jour avec succès' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Erreur lors de la mise à jour du profil' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Mon Profil
      </Typography>

      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informations personnelles
              </Typography>
              
              {/* Photo de profil */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar
                    src={profilePhotoPreview}
                    alt="Photo de profil"
                    sx={{ 
                      width: 120, 
                      height: 120,
                      border: '3px solid',
                      borderColor: 'primary.main'
                    }}
                  />
                  <IconButton
                    color="primary"
                    aria-label="Changer la photo"
                    onClick={handlePhotoClick}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      bgcolor: 'background.paper',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <PhotoCameraIcon />
                  </IconButton>
                </Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  style={{ display: 'none' }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Cliquez sur l'icône pour changer la photo
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="name"
                  label="Nom complet"
                  name="name"
                  value={profile.name}
                  onChange={handleChange}
                />
                
                {/* Champ Email */}
                <TextField
                  margin="normal"
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  type="email"
                  value={profile.email}
                  onChange={handleChange}
                  disabled={user?.signupMethod === 'email'}
                  helperText={user?.signupMethod === 'email' ? 'Cet email ne peut pas être modifié (méthode d\'inscription)' : (user?.signupMethod === 'phone' ? 'Vous pouvez ajouter ou modifier votre email' : '')}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
                      backgroundColor: 'action.disabledBackground'
                    }
                  }}
                />
                
                {/* Champ Téléphone */}
                <TextField
                  margin="normal"
                  fullWidth
                  id="phone"
                  label="Téléphone"
                  name="phone"
                  type="tel"
                  value={profile.phone}
                  onChange={handleChange}
                  disabled={user?.signupMethod === 'phone'}
                  helperText={user?.signupMethod === 'phone' ? 'Ce numéro de téléphone ne peut pas être modifié (méthode d\'inscription)' : (user?.signupMethod === 'email' ? 'Vous pouvez ajouter ou modifier votre numéro de téléphone' : '')}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
                      backgroundColor: 'action.disabledBackground'
                    }
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Mettre à jour'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
      </Grid>
    </Container>
  );
};

export default Profile;
